// The open song and its playback session. The Transport lives here (outside React); the store
// mirrors its state for rendering. Playback position is NOT stored: read it per frame with
// getTransport()?.getPosition() (IMPLEMENTATION_PLAN.md section 2.3).
import { create } from 'zustand';
import { barOfBeat, barRange, beatIndexAt, beatTime } from '@/core/analysis/grid';
import type { AnalysisResult } from '@/core/analysis/types';
import { getAudioContext, unlockAudio } from '@/audio/context';
import { decodeAudio } from '@/audio/decode';
import { sha256Hex } from '@/audio/hash';
import { recallDecoded, recallFile, rememberDecoded, rememberFile } from '@/audio/sessionAudio';
import { Practice, type SpeedTrainer } from '@/audio/practice';
import { Transport, type LoopRange, type PlayerTransport } from '@/audio/transport';
import { audioShiftFor, metronomeEvents, synthEvents } from '@/core/practice/events';
import { getAppSettings } from '@/storage/settingsRepo';
import {
  getAnalysis,
  getSong,
  getSongSettings,
  markOpened,
  updateSongSettings,
} from '@/storage/songsRepo';
import type { Instrument, Song } from '@/storage/types';
import { useSettingsStore } from './settingsStore';

export type PlayerStatus =
  | 'idle'
  | 'loading'
  | 'ready'
  /** The audio was dropped from memory (many large files this session): select it again. */
  | 'needs-file'
  /** Not in this tab's songs, e.g. after a reload (nothing is saved): import the file again. */
  | 'missing'
  | 'error';

export const LOOP_PRESETS = [1, 2, 4, 8] as const;

interface PlayerState {
  songId: string | null;
  status: PlayerStatus;
  song: Song | null;
  result: AnalysisResult | null;
  /** Shown next to the relink file picker, e.g. when the wrong file was chosen. */
  message: string | null;
  playing: boolean;
  rate: number;
  loop: LoopRange | null;
  /** Bars covered by the loop button (last preset used). */
  loopBars: number;
  volume: number;
  muted: boolean;
  visualOffsetMs: number;
  /** Per-song display settings (F5.1, F5.2, F4.8; remembered per song, F5.7). */
  transpose: number;
  capo: number;
  instrument: Instrument;
  /** Pitch-shift the recording with the transpose (F3.3, per song). */
  shiftAudio: boolean;
  /** Practice sounds (F3.6–F3.8, app-wide) and the speed trainer (F9.1, this session). */
  metronome: boolean;
  metronomeVolume: number;
  countIn: boolean;
  synth: boolean;
  synthVolume: number;
  trainer: SpeedTrainer | null;

  open: (songId: string) => Promise<void>;
  /** Plays a YouTube song through its embedded video (the YouTube module's transport). */
  attachVideo: (video: PlayerTransport) => Promise<void>;
  /** The video went away (the player closed): stops using it. */
  detachVideo: (video: PlayerTransport) => void;
  close: () => void;
  relink: (file: File) => Promise<void>;
  togglePlay: () => Promise<void>;
  seek: (seconds: number) => void;
  setRate: (rate: number) => void;
  /** One speed step faster (1) or slower (−1): 5%, or the next speed the video offers. */
  nudgeRate: (direction: 1 | -1) => void;
  setLoopBars: (bars: number | null) => void;
  toggleLoop: () => void;
  setVolume: (volume: number) => void;
  toggleMute: () => void;
  stepBeats: (count: number) => void;
  stepBars: (count: number) => void;
  /** Seeks to the start of a beat (chord grid clicks, F3.1). */
  seekToBeat: (beatIndex: number) => void;
  setTranspose: (semitones: number) => void;
  setCapo: (fret: number) => void;
  setInstrument: (instrument: Instrument) => void;
  setShiftAudio: (on: boolean) => void;
  setPractice: (
    patch: Partial<
      Pick<PlayerState, 'metronome' | 'metronomeVolume' | 'countIn' | 'synth' | 'synthVolume'>
    >,
  ) => void;
  setTrainer: (trainer: SpeedTrainer | null) => void;
}

/** What plays the open song: its audio, or its embedded video. */
let transport: PlayerTransport | null = null;
/** The audio transport, when the song plays from its own audio (pitch shift, practice sounds). */
let audio: Transport | null = null;
let practice: Practice | null = null;
let openToken = 0;

export function getTransport(): PlayerTransport | null {
  return transport;
}

const initial = {
  songId: null,
  status: 'idle' as PlayerStatus,
  song: null,
  result: null,
  message: null,
  playing: false,
  rate: 1,
  loop: null,
  loopBars: 4,
  volume: 1,
  muted: false,
  visualOffsetMs: 0,
  transpose: 0,
  capo: 0,
  instrument: 'guitar' as Instrument,
  shiftAudio: false,
  trainer: null as SpeedTrainer | null,
};

function practicePrefs() {
  const a = useSettingsStore.getState();
  return {
    metronome: a.metronome,
    metronomeVolume: a.metronomeVolume,
    countIn: a.countIn,
    synth: a.synth,
    synthVolume: a.synthVolume,
  };
}

export const usePlayerStore = create<PlayerState>()((set, get) => {
  function disposeTransport() {
    practice?.dispose();
    practice = null;
    transport?.dispose();
    transport = null;
    audio = null;
  }

  /** Semitones the recording is shifted by (0 unless "shift audio" is on). */
  function audioShift(): number {
    const { shiftAudio, transpose } = get();
    return shiftAudio ? audioShiftFor(transpose) : 0;
  }

  /** Applies the practice settings and pitch shift to the open song's sounds. */
  function refreshSound() {
    const { result, metronome, metronomeVolume, synth, synthVolume } = get();
    if (!audio || !practice || !result) return;
    if (audio.semitones !== audioShift()) audio.setSemitones(audioShift());
    practice.setMetronome(metronome, metronomeVolume, metronomeEvents(result.beatGrid));
    practice.setSynth(
      synth,
      synthVolume,
      synthEvents(result.segments, result.beatGrid, audioShift()),
    );
  }

  async function attach(buffer: AudioBuffer, token: number) {
    const { songId } = get();
    if (!songId) return;
    const ctx = getAudioContext();
    // Loaded on demand: the stretch library embeds its WebAssembly and is only needed for playback.
    const { createStretchEngine } = await import('@/audio/stretchEngine');
    const engine = await createStretchEngine(ctx, buffer);
    if (token !== openToken) {
      engine.dispose();
      return;
    }
    disposeTransport();
    const t = new Transport(ctx, engine, buffer.duration);
    audio = t;
    practice = new Practice(ctx, t, (rate) => {
      t.setRate(rate);
    });
    await connect(t, songId, token);
  }

  /** Makes `t` the song's transport: mirrors its state and applies the song's settings. */
  async function connect(t: PlayerTransport, songId: string, token: number) {
    transport = t;
    const sync = () => {
      set({
        playing: t.state === 'playing',
        rate: t.rate,
        loop: t.loop,
        volume: t.volume,
        muted: t.muted,
      });
    };
    for (const event of ['state', 'rate', 'loop', 'volume', 'ended'] as const) t.on(event, sync);

    const settings = await getSongSettings(songId);
    if (token !== openToken) return;
    t.setRate(settings.speed);
    t.setVolume(useSettingsStore.getState().volume);
    if (settings.loop) t.setLoop(settings.loop);
    sync();
    refreshSound();
    set({ status: 'ready', message: null });
  }

  function persist(patch: Parameters<typeof updateSongSettings>[1]) {
    const { songId } = get();
    if (songId) void updateSongSettings(songId, patch);
  }

  function position(): number {
    return transport?.getPosition(get().visualOffsetMs) ?? 0;
  }

  return {
    ...initial,
    ...practicePrefs(),

    async open(songId) {
      const token = ++openToken;
      disposeTransport();
      set({ ...initial, ...practicePrefs(), songId, status: 'loading' });
      try {
        const [song, result, app] = await Promise.all([
          getSong(songId),
          getAnalysis(songId),
          getAppSettings(),
        ]);
        if (token !== openToken) return;
        if (!song || !result) {
          set({ status: 'missing' });
          return;
        }
        const display = await getSongSettings(songId);
        if (token !== openToken) return;
        set({
          song,
          result,
          visualOffsetMs: app.visualOffsetMs,
          transpose: display.transpose,
          capo: display.capo,
          instrument: display.instrument,
          shiftAudio: display.shiftAudio ?? false,
        });
        void markOpened(songId);
        // A YouTube song plays in its embedded video, which attaches itself (attachVideo).
        if (song.youtubeId) {
          set({ status: 'ready' });
          return;
        }
        // Audio is kept in memory for this tab; very large sessions may have dropped it.
        let buffer = recallDecoded(songId);
        if (!buffer) {
          const bytes = recallFile(songId);
          if (bytes) buffer = await decodeAudio(getAudioContext(), bytes);
        }
        if (token !== openToken) return;
        if (!buffer) {
          set({ status: 'needs-file' });
          return;
        }
        rememberDecoded(songId, buffer);
        await attach(buffer, token);
      } catch (error) {
        console.error(error);
        if (token === openToken)
          set({ status: 'error', message: 'This song could not be loaded.' });
      }
    },

    async attachVideo(video) {
      const { songId, song } = get();
      if (!songId || !song?.youtubeId) return;
      disposeTransport();
      await connect(video, songId, openToken);
    },

    detachVideo(video) {
      if (transport !== video) return;
      disposeTransport();
      set({ playing: false });
    },

    close() {
      openToken++;
      disposeTransport();
      set({ ...initial });
    },

    async relink(file) {
      const { songId } = get();
      if (!songId) return;
      const token = openToken;
      const bytes = await file.arrayBuffer();
      if ((await sha256Hex(bytes)) !== songId) {
        set({ message: `"${file.name}" is a different file. Choose the same file you analyzed.` });
        return;
      }
      try {
        const buffer = await decodeAudio(getAudioContext(), bytes);
        if (token !== openToken) return;
        rememberFile(songId, bytes);
        rememberDecoded(songId, buffer);
        set({ status: 'loading', message: null });
        await attach(buffer, token);
      } catch {
        set({ message: "This browser can't decode that file." });
      }
    },

    async togglePlay() {
      if (!transport) return;
      await unlockAudio();
      const { countIn, metronomeVolume, result } = get();
      if (audio?.state === 'paused' && countIn && practice && result) {
        // Start on the current beat, after one bar of clicks at the playing speed.
        const grid = result.beatGrid;
        const beat = beatIndexAt(grid, audio.positionAt(audio.anchor.output));
        if (beat >= 0) audio.seek(beatTime(grid, beat, audio.duration));
        const beatSeconds = 60 / grid.bpm / audio.rate;
        practice.countInAndPlay(grid.beatsPerBar, beatSeconds, Math.max(metronomeVolume, 0.4));
        return;
      }
      transport.toggle();
    },

    seek(seconds) {
      transport?.seek(seconds);
    },

    setRate(rate) {
      if (!transport) return;
      transport.setRate(Math.round(rate * 20) / 20);
      persist({ speed: transport.rate });
    },

    nudgeRate(direction) {
      if (!transport) return;
      const steps = transport.rateSteps;
      if (!steps) {
        get().setRate(transport.rate + direction * 0.05);
        return;
      }
      const i = steps.indexOf(transport.rate);
      const next = steps[Math.max(0, Math.min(steps.length - 1, i + direction))];
      if (next !== undefined) get().setRate(next);
    },

    setLoopBars(bars) {
      const { result, song } = get();
      if (!transport || !result || !song) return;
      if (bars === null) {
        transport.setLoop(null);
        persist({ loop: null });
        return;
      }
      const grid = result.beatGrid;
      const beat = Math.max(0, beatIndexAt(grid, position()));
      const bar = Math.max(1, barOfBeat(grid, beat));
      const range = barRange(grid, bar, bar + bars - 1, transport.duration);
      transport.setLoop(range);
      set({ loopBars: bars });
      persist({ loop: range });
    },

    toggleLoop() {
      get().setLoopBars(get().loop ? null : get().loopBars);
    },

    setVolume(volume) {
      transport?.setVolume(volume);
      useSettingsStore.getState().update({ volume: transport?.volume ?? volume });
      if (transport?.muted && volume > 0) transport.setMuted(false);
    },

    toggleMute() {
      if (transport) transport.setMuted(!transport.muted);
    },

    stepBeats(count) {
      const { result } = get();
      if (!transport || !result) return;
      const grid = result.beatGrid;
      const current = beatIndexAt(grid, position() + 0.02);
      const target = Math.max(0, current + count);
      transport.seek(beatTime(grid, target, transport.duration));
    },

    stepBars(count) {
      const { result } = get();
      if (!transport || !result) return;
      const grid = result.beatGrid;
      const current = Math.max(0, beatIndexAt(grid, position() + 0.02));
      const barStart =
        current -
        ((((current - grid.firstDownbeat) % grid.beatsPerBar) + grid.beatsPerBar) %
          grid.beatsPerBar);
      const target = Math.max(0, barStart + count * grid.beatsPerBar);
      transport.seek(beatTime(grid, target, transport.duration));
    },

    seekToBeat(beatIndex) {
      const { result } = get();
      if (!transport || !result) return;
      transport.seek(beatTime(result.beatGrid, beatIndex, transport.duration));
    },

    setTranspose(semitones) {
      const transpose = Math.max(-11, Math.min(11, Math.round(semitones)));
      set({ transpose });
      persist({ transpose });
      if (get().shiftAudio) refreshSound();
    },

    setShiftAudio(shiftAudio) {
      set({ shiftAudio });
      persist({ shiftAudio });
      refreshSound();
    },

    setPractice(patch) {
      set(patch);
      useSettingsStore.getState().update(patch);
      refreshSound();
    },

    setTrainer(trainer) {
      set({ trainer });
      practice?.setTrainer(trainer);
    },

    setCapo(fret) {
      const capo = Math.max(0, Math.min(11, Math.round(fret)));
      set({ capo });
      persist({ capo });
    },

    setInstrument(instrument) {
      set({ instrument });
      persist({ instrument });
      // The instrument you pick is the one you play: it becomes the default for every song.
      useSettingsStore.getState().update({ defaultInstrument: instrument });
    },
  };
});
